import { useMemo } from 'react';

import {
  AppOfferListItem,
  LoyaltyEngineId,
  LoyaltyOffer,
  SanityOfferListItem,
  SanitySystemwideOffer,
  appOfferToLoyaltyOffer,
  isAppOffer,
  isAppOfferGroup,
  isAppPersonalizedOffer,
  isAppPersonalizedOfferGroup,
  isSanityOfferGroup,
  isSanitySystemwideOffer,
  sanitySystemwideOfferToLoyaltyOffer,
} from 'state/loyalty/types';

import { useGetCmsAndLoyaltyOffers } from './use-get-cms-and-loyalty-offers';
import { useReduceOffers } from './use-offers-reducer';

type SelectedIncentive = (LoyaltyOffer & { isUnavailable?: boolean }) | undefined;

function findAppOfferById(items: AppOfferListItem[], id: LoyaltyEngineId) {
  for (const item of items) {
    if (isAppOffer(item) && item.loyaltyEngineId === id) {
      return item;
    }

    // This allows us to create deeplinks to personalized offer using the loyalty engine
    // id of the config offer (configId is the loyaltyEngineId of the config offer)
    if (isAppPersonalizedOffer(item) && item.configId === id) {
      return item;
    }

    if (isAppOfferGroup(item)) {
      const child = item.offers?.find(o => o.loyaltyEngineId === id);
      if (child) {
        return child;
      }
    }
    if (isAppPersonalizedOfferGroup(item)) {
      const child = item.offers?.find(o => o.loyaltyEngineId === id);
      if (child) {
        return child;
      }
    }
  }
}

function findSanitySystemwideOfferById(
  items: SanityOfferListItem[],
  id: LoyaltyEngineId
): SanitySystemwideOffer | undefined {
  for (const item of items) {
    if (isSanitySystemwideOffer(item) && item.loyaltyEngineId === id) {
      return item;
    }
    if (isSanityOfferGroup(item)) {
      const child = item.offers?.find(o => o.loyaltyEngineId === id);
      if (child) {
        return child;
      }
    }
  }
  return undefined;
}

export const useGetIncentive = ({ loyaltyEngineId }: { loyaltyEngineId: LoyaltyEngineId }) => {
  const {
    sanityOfferListItems,
    appPersonalizedOffers,
    appPersonalizedOfferGroups,
    engineOfferListItems,
    offersLoading,
    refreshOffersLoading,
  } = useGetCmsAndLoyaltyOffers();

  const { offerListItems, offersInCart, reductionLoading } = useReduceOffers({
    sanityOfferListItems,
    appPersonalizedOffers,
    appPersonalizedOfferGroups,
    engineOfferListItems,
    offersLoading,
    refreshOffersLoading,
  });

  const selectedIncentive: SelectedIncentive = useMemo(() => {
    // 1) Check offers in cart first
    const offerInCart = offersInCart.find(offer => offer.loyaltyEngineId === loyaltyEngineId);
    if (offerInCart) {
      return appOfferToLoyaltyOffer(offerInCart);
    }

    // 2) Search in offer list items
    const offerInList = findAppOfferById(offerListItems, loyaltyEngineId);
    if (offerInList) {
      return appOfferToLoyaltyOffer(offerInList);
    }

    // 3) Search in sanity list items (mark as unavailable if found)
    const sanityOffer = findSanitySystemwideOfferById(sanityOfferListItems, loyaltyEngineId);
    if (sanityOffer) {
      return { ...sanitySystemwideOfferToLoyaltyOffer(sanityOffer), isUnavailable: true };
    }

    return undefined;
  }, [loyaltyEngineId, offerListItems, offersInCart, sanityOfferListItems]);

  const isIncentivesLoading =
    offersLoading ||
    reductionLoading ||
    refreshOffersLoading ||
    !offerListItems ||
    offerListItems?.length === 0;

  return { selectedIncentive, isIncentivesLoading };
};
